import { useCallback, useEffect, useMemo, useState } from "react";

import { api, type KnowledgeItemData, type KnowledgeItemListResponse } from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";

// バックエンド KnowledgeItemIndexRequest の limit 上限（max:50）に合わせる。
const EXECUTIVE_MEMO_KNOWLEDGE_PAGE_LIMIT = 50;
const MEMO_ID_KEY_SEPARATOR = "\u001f";

export type ExecutiveMemoKnowledgeLookupStatus = "idle" | "loading" | "loaded" | "error";

export function isReadyExecutiveMemoKnowledgeItemForMemo(item: KnowledgeItemData, memoId: string): boolean {
  return (
    item.type === "executive_memo" &&
    item.source_type === "executive_memo" &&
    item.source_id === memoId &&
    item.status === "ready"
  );
}

function buildMemoIdKey(memoIds: string[]): string {
  return Array.from(new Set(memoIds.filter(Boolean))).sort().join(MEMO_ID_KEY_SEPARATOR);
}

function memoIdsFromKey(memoIdKey: string): string[] {
  return memoIdKey ? memoIdKey.split(MEMO_ID_KEY_SEPARATOR) : [];
}

function isAbortError(error: unknown): boolean {
  return Boolean(error && typeof error === "object" && (error as { name?: string }).name === "AbortError");
}

function buildReadyExecutiveMemoKnowledgeMap(items: KnowledgeItemData[], memoIds: string[]) {
  const memoIdSet = new Set(memoIds);
  const next: Record<string, KnowledgeItemData> = {};

  for (const item of items) {
    const memoId = item.source_id ?? "";
    if (!memoIdSet.has(memoId)) continue;
    if (!isReadyExecutiveMemoKnowledgeItemForMemo(item, memoId)) continue;
    if (next[memoId]) continue;

    next[memoId] = item;
  }

  return next;
}

async function listReadyExecutiveMemoKnowledgeItems(signal?: AbortSignal): Promise<KnowledgeItemData[]> {
  const items: KnowledgeItemData[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null | undefined = null;

  do {
    const response: KnowledgeItemListResponse = await api.listKnowledgeItems(
      {
        type: "executive_memo",
        status: "ready",
        limit: EXECUTIVE_MEMO_KNOWLEDGE_PAGE_LIMIT,
        cursor,
      },
      signal
    );

    items.push(...response.items);
    cursor = response.next_cursor ?? null;
    if (cursor) {
      if (seenCursors.has(cursor)) break;
      seenCursors.add(cursor);
    }
  } while (cursor);

  return items;
}

export function useExecutiveMemoKnowledgeState(memoIds: string[]) {
  const memoIdKey = buildMemoIdKey(memoIds);
  const normalizedMemoIds = useMemo(() => memoIdsFromKey(memoIdKey), [memoIdKey]);
  const [reloadToken, setReloadToken] = useState(0);
  const [status, setStatus] = useState<ExecutiveMemoKnowledgeLookupStatus>(
    normalizedMemoIds.length > 0 ? "loading" : "idle"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [itemsByMemoId, setItemsByMemoId] = useState<Record<string, KnowledgeItemData>>({});
  const [settledMemoIdKey, setSettledMemoIdKey] = useState("");

  useEffect(() => {
    if (normalizedMemoIds.length === 0) {
      setStatus("idle");
      setErrorMessage(null);
      setItemsByMemoId({});
      setSettledMemoIdKey("");
      return;
    }

    const controller = new AbortController();
    setStatus("loading");
    setErrorMessage(null);

    listReadyExecutiveMemoKnowledgeItems(controller.signal)
      .then((items) => {
        setItemsByMemoId(buildReadyExecutiveMemoKnowledgeMap(items, normalizedMemoIds));
        setSettledMemoIdKey(memoIdKey);
        setStatus("loaded");
      })
      .catch((error) => {
        if (isAbortError(error)) return;

        logError(error);
        setItemsByMemoId({});
        setSettledMemoIdKey(memoIdKey);
        setErrorMessage(toUserMessage(error));
        setStatus("error");
      });

    return () => {
      controller.abort();
    };
  }, [memoIdKey, normalizedMemoIds, reloadToken]);

  const rememberKnowledgeItem = useCallback((item: KnowledgeItemData) => {
    const memoId = item.source_id ?? "";
    if (!isReadyExecutiveMemoKnowledgeItemForMemo(item, memoId)) return;

    setItemsByMemoId((prev) => ({ ...prev, [memoId]: item }));
    setStatus((current) => (current === "error" ? "loaded" : current));
    setErrorMessage(null);
  }, []);

  const reload = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  const effectiveStatus: ExecutiveMemoKnowledgeLookupStatus =
    normalizedMemoIds.length === 0 ? "idle" : settledMemoIdKey === memoIdKey ? status : "loading";

  return {
    status: effectiveStatus,
    errorMessage: effectiveStatus === "error" ? errorMessage : null,
    itemsByMemoId: effectiveStatus === "loading" ? {} : itemsByMemoId,
    rememberKnowledgeItem,
    reload,
  };
}
